import type { LLMTrace } from '../context/llm-data-context';

export type TraceRow = LLMTrace & {
	isChild?: boolean;
};

export type SortableFields = Exclude<
	keyof LLMTrace,
	| 'children'
	| 'error_message'
	| 'metadata'
	| 'input_messages'
	| 'output_content'
	| 'system_prompt'
>;

export interface FieldConfig {
	key: SortableFields;
	label: string;
	sortable: boolean;
	formatter?: (value: unknown, trace: TraceRow) => string | React.ReactNode;
	className?: string;
}

// Formatter functions
export const formatters = {
	date: (date: unknown) => new Date(date as string).toLocaleString(),

	functionId: (id: unknown) => {
		// Trim whitespace and normalize the function ID
		const normalizedId = id?.toString().trim().toLowerCase();

		const getStyles = () => {
			switch (normalizedId) {
				case 'askrev':
					return { backgroundColor: '#dbeafe', color: '#1e40af' }; // blue
				case 'revbot':
					return { backgroundColor: '#fed7aa', color: '#ea580c' }; // orange
				case 'autopilot':
					return { backgroundColor: '#f3e8ff', color: '#7c3aed' }; // purple
				default:
					return { backgroundColor: '#f3f4f6', color: '#1f2937' }; // gray
			}
		};

		return (
			<span
				className='tw-px-2 tw-py-1 tw-rounded tw-text-xs tw-font-medium'
				style={getStyles()}
			>
				{id?.toString()}
			</span>
		);
	},

	modelName: (name: unknown, trace: TraceRow) => {
		// First try to get model name from metadata (same logic as modal)
		let modelName = '';
		if (trace.metadata && typeof trace.metadata === 'object') {
			const metadata = trace.metadata as Record<string, unknown>;
			if (metadata.modelId && typeof metadata.modelId === 'string') {
				// Extract the model name from modelId like "anthropic.claude-3-5-haiku-20241022-v1:0"
				if (metadata.modelId.includes('anthropic.')) {
					modelName = metadata.modelId.replace('anthropic.', '');
				} else {
					modelName = metadata.modelId;
				}
			}
		}

		// Enhanced fallback parsing for AWS Bedrock ARNs and other formats
		if (!modelName) {
			const nameString = (name as string) || '';

			// Handle AWS Bedrock ARN format: arn:aws:bedrock:region:account:application-inference-profile/model-name
			if (nameString.includes('arn:aws:bedrock')) {
				// Extract everything after the last '/' which should be the model identifier
				const parts = nameString.split('/');
				if (parts.length > 1) {
					const modelPart = parts[parts.length - 1];
					// If it still contains anthropic prefix, remove it
					if (modelPart.includes('anthropic.')) {
						modelName = modelPart.replace('anthropic.', '');
					} else {
						modelName = modelPart;
					}
				} else {
					modelName = nameString.split('/').pop() || nameString;
				}
			} else if (nameString.includes('anthropic.')) {
				// Handle direct anthropic model names
				modelName = nameString.replace('anthropic.', '');
			} else {
				// Return as-is if no special formatting needed
				modelName = nameString;
			}
		}

		// Truncate if over 64 characters and add tooltip
		const displayName =
			modelName.length > 64 ? `${modelName.substring(0, 61)}...` : modelName;

		return (
			<span title={modelName} className='tw-cursor-help'>
				{displayName}
			</span>
		);
	},

	environment: (env: unknown) => (
		<span
			className={`tw-px-2 tw-py-1 tw-rounded tw-text-xs tw-font-medium ${
				env === 'production'
					? 'tw-bg-red-100 tw-text-red-800'
					: env === 'development'
						? 'tw-bg-yellow-100 tw-text-yellow-800'
						: env === 'bestrev'
							? 'tw-bg-green-100 tw-text-green-800'
							: env === 'rmws'
								? 'tw-bg-blue-100 tw-text-blue-800'
								: 'tw-bg-gray-100 tw-text-gray-800'
			}`}
		>
			{env as string}
		</span>
	),

	status: (status: unknown) => {
		const getStatusStyles = () => {
			switch (status) {
				case 'completed':
					return { backgroundColor: '#dcfce7', color: '#166534' }; // green
				case 'error':
				case 'failed':
					return { backgroundColor: '#fee2e2', color: '#dc2626' }; // red
				case 'pending':
					return { backgroundColor: '#fef3c7', color: '#d97706' }; // yellow
				default:
					return { backgroundColor: '#f3f4f6', color: '#1f2937' }; // gray
			}
		};

		return (
			<span
				className='tw-px-2 tw-py-1 tw-rounded tw-text-xs tw-font-medium'
				style={getStatusStyles()}
			>
				{status as string}
			</span>
		);
	},

	tokens: (total: unknown, trace: TraceRow) => (
		<div className='tw-text-sm'>
			<div className='tw-text-xs tw-text-gray-500'>
				{trace.input_tokens.toLocaleString()} in /{' '}
				{trace.output_tokens.toLocaleString()} out
			</div>
			<div className='tw-font-medium'>
				{(total as number).toLocaleString()} total
			</div>
		</div>
	),

	cost: (cost: unknown) => `$${Number.parseFloat(cost as string).toFixed(6)}`,

	latency: (ms: unknown) => (ms as number).toLocaleString(),

	children: (_: unknown, trace: TraceRow) => {
		// Always show count - simple and direct
		const childCount = trace.children?.length || 0;

		if (trace.isChild) {
			return <span className='tw-text-gray-400 tw-text-sm'>-</span>;
		}

		if (childCount > 0) {
			return (
				<span className='tw-px-2 tw-py-1 tw-rounded tw-text-xs tw-font-medium tw-bg-green-100 tw-text-green-800'>
					{childCount}
				</span>
			);
		}

		return <span className='tw-text-gray-400 tw-text-sm'>0</span>;
	},

	app: (_: unknown, trace: TraceRow) => {
		const getAppName = (trace: TraceRow) => {
			if (trace.metadata && typeof trace.metadata === 'object') {
				const metadata = trace.metadata as Record<string, unknown>;
				return typeof metadata.app === 'string' ? metadata.app : 'Unknown';
			}
			return 'Unknown';
		};

		const appName = getAppName(trace);
		return (
			<span className='tw-px-2 tw-py-1 tw-rounded tw-text-xs tw-font-medium tw-bg-purple-100 tw-text-purple-800'>
				{appName}
			</span>
		);
	},
};

// Dynamic field configuration derived from LLMTrace interface
export const FIELD_CONFIGS: (
	| FieldConfig
	| {
			key: null;
			label: string;
			sortable: false;
			className?: string;
			formatter?: (value: unknown, trace: TraceRow) => string | React.ReactNode;
	  }
)[] = [
	{ key: null, label: '', sortable: false, className: 'tw-w-8' }, // Expansion toggle
	{
		key: 'created_at',
		label: 'Created At',
		sortable: true,
		formatter: formatters.date,
		className: 'tw-text-xs',
	},
	{
		key: 'function_id',
		label: 'Function',
		sortable: true,
		formatter: formatters.functionId,
	},
	{
		key: 'model_name',
		label: 'Model',
		sortable: true,
		formatter: formatters.modelName,
		className: 'tw-text-xs',
	},
	{
		key: 'environment',
		label: 'Environment',
		sortable: true,
		formatter: formatters.environment,
	},
	{
		key: 'status',
		label: 'Status',
		sortable: true,
		formatter: formatters.status,
	},
	{
		key: 'total_tokens',
		label: 'Tokens',
		sortable: true,
		formatter: formatters.tokens,
	},
	{
		key: 'total_cost',
		label: 'Cost',
		sortable: true,
		formatter: formatters.cost,
		className: 'tw-font-mono',
	},
	{
		key: 'latency_ms',
		label: 'Latency (ms)',
		sortable: true,
		formatter: formatters.latency,
	},
	{
		key: 'session_id',
		label: 'Session ID',
		sortable: false,
		className: 'tw-font-mono tw-text-xs',
	},
	{
		key: null,
		label: 'Children',
		sortable: false,
		formatter: formatters.children,
		className: 'tw-text-center',
	},
	{
		key: null,
		label: 'App',
		sortable: false,
		formatter: formatters.app,
	},
];
